import {
  Form, FormProps, SelectOption,
} from '@doveit/bricks';
import React from 'react';
import { subYears } from 'date-fns';
import { formatDate } from '@doveit/hammer';
import { asSelectOptions } from '../../../../../select-options/utils/asSelectOptions';
import { identityDocumentTypeLabels } from '../../../../../labels/identityDocumentTypeLabels';
import { INPUT_PLACEHOLDERS } from '../../../../../constants';
import { formatInputDate } from '../../../../../utils/form';
import { IdentityDocumentType } from '../../../../../domain/types';
import { Contact } from '../../../../../providers/api/dtos';
import { contactToAssignmentWizardPlanimetryDelegationFormModel } from './mappers';

export interface AssignmentWizardPlanimetryDelegationFormModel {
  planimetryDelegationRequired: boolean,
  contactId: string,
  identityDocumentType: IdentityDocumentType | '',
  identityDocumentNumber: string,
  identityDocumentIssuedBy: string,
  identityDocumentIssuedAt: string,
}

export interface AssignmentWizardPlanimetryDelegationFormProps {
  initialValues?: Partial<AssignmentWizardPlanimetryDelegationFormModel>,
  loading?: FormProps<AssignmentWizardPlanimetryDelegationFormModel>['loading'],
  disabled?: FormProps<AssignmentWizardPlanimetryDelegationFormModel>['disabled'],
  onSubmit: (values: AssignmentWizardPlanimetryDelegationFormModel) => void,
  formRef: FormProps<AssignmentWizardPlanimetryDelegationFormModel>['innerRef'],
  contacts?: Contact[],
}
// @TODO: move to more generic file
export const IDENTITY_DOCUMENT_TYPE_PLACEHOLDER: Record<IdentityDocumentType, string> = {
  IDENTITY_CARD: INPUT_PLACEHOLDERS.CONTACT_IDENTITY_CARD_NUMBER,
  PASSPORT: INPUT_PLACEHOLDERS.CONTACT_PASSPORT_NUMBER,
  DRIVER_LICENSE: INPUT_PLACEHOLDERS.CONTACT_DRIVER_LICENSE_NUMBER,
};

// @TODO: move to more generic file
export const IDENTITY_DOCUMENT_ISSUED_BY_PLACEHOLDER: Record<IdentityDocumentType, string> = {
  IDENTITY_CARD: INPUT_PLACEHOLDERS.CONTACT_IDENTITY_CARD_ISSUED_BY,
  PASSPORT: INPUT_PLACEHOLDERS.CONTACT_PASSPORT_ISSUED_BY,
  DRIVER_LICENSE: INPUT_PLACEHOLDERS.CONTACT_DRIVER_LICENSE_ISSUED_BY,
};

const DEFAULT_INITIAL_VALUES: AssignmentWizardPlanimetryDelegationFormModel = {
  planimetryDelegationRequired: true,
  contactId: '',
  identityDocumentType: '',
  identityDocumentNumber: '',
  identityDocumentIssuedBy: '',
  identityDocumentIssuedAt: '',
};

const AssignmentWizardPlanimetryDelegationForm: React.FC<AssignmentWizardPlanimetryDelegationFormProps> = ({
  initialValues = {},
  formRef,
  contacts = [],
  ...rest
}) => {
  const [oldInitialValues, setOldInitialValues] = React.useState<Partial<AssignmentWizardPlanimetryDelegationFormModel>>();

  React.useEffect(() => {
    const oldValues = contactToAssignmentWizardPlanimetryDelegationFormModel(contacts.find(({ id }) => id === initialValues.contactId));

    setOldInitialValues(oldValues);
  }, [contacts, initialValues.contactId]);

  const mergedInitialValues: AssignmentWizardPlanimetryDelegationFormModel = React.useMemo(() => ({
    ...DEFAULT_INITIAL_VALUES,
    ...initialValues,
    planimetryDelegationRequired: contacts.length === 0 ? false : Boolean(initialValues.planimetryDelegationRequired),
  }), [contacts.length, initialValues]);

  const contactsOptions: SelectOption[] = React.useMemo(
    () => [
      {
        label: 'Seleziona',
        value: '',
        disabled: true,
      },
      ...contacts.map((contact, i) => ({
        value: contact.id || '',
        label: contact.name || contact.email || contact.phoneNumber || `Venditore ${i}`,
      })),
    ],
    [contacts],
  );

  const minIdentityDocumentIssuedAt = React.useMemo(() => formatInputDate(subYears(new Date(), 10)), []);
  const today = React.useMemo(() => formatInputDate(new Date()), []);

  return (
    <Form
      initialValues={mergedInitialValues}
      innerRef={formRef}
      enableReinitialize
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const onContactChange: React.ChangeEventHandler<HTMLSelectElement> = ({ target }) => {
          const selectedContact = contacts.find((contact) => contact.id === target.value);

          setFieldValue('contactId', selectedContact?.id || '', false);
          setFieldValue('identityDocumentType', selectedContact?.identityDocument?.type || '', false);
          setFieldValue('identityDocumentNumber', selectedContact?.identityDocument?.number || '', false);
          setFieldValue('identityDocumentIssuedBy', selectedContact?.identityDocument?.issuedBy || '', false);
          setFieldValue('identityDocumentIssuedAt', selectedContact?.identityDocument?.issuedAt || '', false);

          setOldInitialValues(contactToAssignmentWizardPlanimetryDelegationFormModel(selectedContact));
        };

        return (
          <>
            <Form.Item size={{ MD: 1 / 3 }}>
              <Form.CheckboxButton
                name="planimetryDelegationRequired"
                text="Aggiungi delega"
                aria-label="Campo per mostrare il form di aggiunta delega"
                disabled={contacts.length === 0}
              />
            </Form.Item>
            {values.planimetryDelegationRequired && contacts.length > 0 && (
              <Form.Fieldset legend="Delegante e documento di riconoscimento">
                <Form.Item size={{ MD: 2 / 3 }}>
                  <Form.Select
                    name="contactId"
                    label="Delegante"
                    aria-label="Campo per selezionare il delegante"
                    options={contactsOptions}
                    onChange={onContactChange}
                    disabled={contacts.length === 1}
                  />
                </Form.Item>
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Select
                    name="identityDocumentType"
                    label="Tipo di documento"
                    aria-label="Campo per selezionare la tipologia di documento di riconoscimento"
                    options={asSelectOptions(identityDocumentTypeLabels)}
                    oldValue={oldInitialValues?.identityDocumentType && oldInitialValues.identityDocumentType !== values.identityDocumentType
                      ? identityDocumentTypeLabels[oldInitialValues.identityDocumentType as IdentityDocumentType]
                      : undefined}
                  />
                </Form.Item>
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Input
                    name="identityDocumentNumber"
                    label="Numero"
                    aria-label="Campo per inserire il numero del documento di riconoscimento"
                    placeholder={values.identityDocumentType ? IDENTITY_DOCUMENT_TYPE_PLACEHOLDER[values.identityDocumentType as IdentityDocumentType] : 'Es. XXXXXXXXX'}
                    oldValue={oldInitialValues?.identityDocumentNumber !== values.identityDocumentNumber ? oldInitialValues?.identityDocumentNumber : undefined}
                  />
                </Form.Item>
                <Form.Item size={{ MD: 2 / 3 }}>
                  <Form.Input
                    name="identityDocumentIssuedBy"
                    label="Rilasciato da"
                    aria-label="Campo per inserire l'ente di emissione del documento di riconoscimento"
                    placeholder={values.identityDocumentType ? IDENTITY_DOCUMENT_ISSUED_BY_PLACEHOLDER[values.identityDocumentType as IdentityDocumentType] : ''}
                    oldValue={oldInitialValues?.identityDocumentIssuedBy !== values.identityDocumentIssuedBy ? oldInitialValues?.identityDocumentIssuedBy : undefined}
                  />
                </Form.Item>
                <Form.Item size={{ MD: 1 / 3 }}>
                  <Form.Input
                    name="identityDocumentIssuedAt"
                    type="date"
                    label="Data rilascio"
                    aria-label="Campo per selezionare la data di rilascio del documento di riconoscimento"
                    min={minIdentityDocumentIssuedAt}
                    max={today}
                    oldValue={oldInitialValues?.identityDocumentIssuedAt && oldInitialValues?.identityDocumentIssuedAt !== values.identityDocumentIssuedAt
                      ? formatDate(new Date(oldInitialValues.identityDocumentIssuedAt))
                      : undefined}
                  />
                </Form.Item>
              </Form.Fieldset>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default AssignmentWizardPlanimetryDelegationForm;
